import { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft, Home, ExternalLink } from 'lucide-react'
import { marked } from 'marked'

interface DocMetadata {
	title?: string
	description?: string
	sidebarTitle?: string
}

// Configure marked options
marked.setOptions({
	breaks: true,
	gfm: true,
})

// Convert Mintlify-specific JSX components to standard HTML/Markdown
function convertMintlifyComponents(content: string): string {
	let converted = content
	
	// Convert <Frame> to markdown image with caption
	converted = converted.replace(
		/<Frame(?:\s+caption=["']([^"']+)["'])?\s*>([\s\S]*?)<\/Frame>/g,
		(_match, caption, innerContent) => {
			// Extract img tag from inner content
			const imgMatch = innerContent.match(/<img[^>]*src=["']([^"']+)["'][^>]*alt=["']([^"']+)["'][^>]*\/>/)
			if (imgMatch) {
				const [, src, alt] = imgMatch
				return caption 
					? `![${alt}](${src})\n\n*${caption}*\n`
					: `![${alt}](${src})\n`
			}
			return innerContent
		}
	)
	
	// Convert <CardGroup> to simple div
	converted = converted.replace(/<CardGroup[^>]*>([\s\S]*?)<\/CardGroup>/g, '$1')
	
	// Convert <Card> to markdown links with descriptions
	converted = converted.replace(
		/<Card\s+title=["']([^"']+)["'](?:\s+icon=["'][^"']+["'])?\s+href=["']([^"']+)["']\s*>?([\s\S]*?)<\/Card>/g,
		'### [$1]($2)\n\n$3\n'
	)
	
	// Convert self-closing <Card> tags
	converted = converted.replace(
		/<Card\s+title=["']([^"']+)["'](?:\s+icon=["'][^"']+["'])?\s+href=["']([^"']+)["']\s*\/>/g,
		'### [$1]($2)\n'
	)
	
	// Convert <Tabs> and <Tab> to sections
	converted = converted.replace(/<Tabs[^>]*>([\s\S]*?)<\/Tabs>/g, '$1')
	converted = converted.replace(
		/<Tab\s+title=["']([^"']+)["']\s*>([\s\S]*?)<\/Tab>/g,
		'\n#### $1\n\n$2\n'
	)
	
	// Convert <CodeGroup> to just the code blocks inside
	converted = converted.replace(/<CodeGroup[^>]*>([\s\S]*?)<\/CodeGroup>/g, '$1')
	
	// Convert <Accordion> to expandable sections
	converted = converted.replace(
		/<Accordion\s+title=["']([^"']+)["']\s*>([\s\S]*?)<\/Accordion>/g,
		'\n<details>\n<summary>$1</summary>\n\n$2\n</details>\n'
	)
	
	// Remove any remaining unmatched JSX-style tags
	converted = converted.replace(/<\/?(Frame|CardGroup|Card|Tabs|Tab|CodeGroup|Accordion)[^>]*>/g, '')
	
	return converted
}

export default function DocsPage() {
	const { '*': docPath } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const [htmlContent, setHtmlContent] = useState<string>('')
	const [metadata, setMetadata] = useState<DocMetadata>({})
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [fullPath, setFullPath] = useState<string>('')

	useEffect(() => {
		const loadDoc = async () => {
			if (!docPath) {
				navigate('/docs')
				return
			}

			setLoading(true)
			setError(null)

			try {
				// Get the full path from the URL, removing leading slash
				const path = location.pathname.replace(/^\//, '')
				setFullPath(path)
				
				// Fetch the raw MDX file from the docs directory
				const response = await fetch(`/docs-content/${path}.mdx`)
				
				if (!response.ok) {
					throw new Error('Documentation page not found')
				}

				const text = await response.text()
				
				// Parse frontmatter
				const frontmatterMatch = text.match(/^---\n([\s\S]*?)\n---/)
				let markdownContent = text
				
				if (frontmatterMatch) {
					const frontmatter = frontmatterMatch[1]
					const meta: DocMetadata = {}
					
					const titleMatch = frontmatter.match(/title:\s*["'](.+?)["']/)
					const descMatch = frontmatter.match(/description:\s*["'](.+?)["']/)
					const sidebarMatch = frontmatter.match(/sidebarTitle:\s*["'](.+?)["']/)
					
					if (titleMatch) meta.title = titleMatch[1]
					if (descMatch) meta.description = descMatch[1]
					if (sidebarMatch) meta.sidebarTitle = sidebarMatch[1]
					
					setMetadata(meta)
					
					// Remove frontmatter from content
					markdownContent = text.replace(/^---\n[\s\S]*?\n---\n/, '')
				}
				
				// Convert Mintlify-specific JSX components to standard HTML
				markdownContent = convertMintlifyComponents(markdownContent)
				
				// Parse markdown to HTML
				const html = await marked.parse(markdownContent)
				setHtmlContent(html)
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to load documentation')
			} finally {
				setLoading(false)
			}
		}

		loadDoc()
	}, [docPath, navigate])

	if (loading) {
		return (
			<div className="min-h-screen bg-vscode-bg flex items-center justify-center">
				<div className="text-vscode-text-muted">Loading documentation...</div>
			</div>
		)
	}

	if (error) {
		return (
			<div className="min-h-screen bg-vscode-bg">
				<div className="max-w-4xl mx-auto px-4 py-20">
					<div className="bg-vscode-editor border border-vscode-border rounded-lg p-8">
						<h1 className="text-3xl font-semibold text-vscode-text-bright mb-4">Page Not Found</h1>
						<p className="text-vscode-text-muted mb-6">{error}</p>
						<button
							onClick={() => navigate('/docs')}
							className="px-6 py-3 bg-vscode-accent hover:bg-vscode-text-bright hover:text-vscode-bg text-white rounded-lg transition-colors font-medium inline-flex items-center gap-2"
						>
							<Home className="w-4 h-4" />
							Back to Documentation
						</button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="min-h-screen bg-vscode-bg">
			{/* Header */}
			<div className="bg-vscode-editor border-b border-vscode-border">
				<div className="max-w-6xl mx-auto px-4 py-6">
					<div className="flex items-center gap-4">
						<button
							onClick={() => navigate('/docs')}
							className="text-vscode-text-muted hover:text-vscode-text-bright transition-colors"
						>
							<Home className="w-5 h-5" />
						</button>
						<div className="text-vscode-text-muted">/</div>
						<div className="text-vscode-accent font-mono text-sm">
							{docPath?.replace(/\//g, ' / ')}
						</div>
					</div>
				</div>
			</div>

			{/* Content */}
			<div className="max-w-4xl mx-auto px-4 py-12">
				<article className="bg-vscode-editor border border-vscode-border rounded-lg p-8 md:p-12">
					{/* Title */}
					{metadata.title && (
						<div className="mb-8 pb-8 border-b border-vscode-border">
							<h1 className="text-4xl md:text-5xl font-semibold text-vscode-text-bright mb-4">
								{metadata.title}
							</h1>
							{metadata.description && (
								<p className="text-lg text-vscode-text-muted">
									{metadata.description}
								</p>
							)}
						</div>
					)}

					{/* Markdown Content */}
					<div 
						className="prose prose-invert prose-vscode max-w-none"
						dangerouslySetInnerHTML={{ __html: htmlContent }}
					/>

					{/* Footer */}
					<div className="mt-12 pt-8 border-t border-vscode-border">
						<div className="flex items-center justify-between">
							<button
								onClick={() => window.history.back()}
								className="text-vscode-accent hover:text-vscode-text-bright transition-colors inline-flex items-center gap-2"
							>
								<ChevronLeft className="w-4 h-4" />
								Back
							</button>
							<a
								href={`https://github.com/rahmanazhar/SakaDev/blob/main/docs/${fullPath}.mdx`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-vscode-text-muted hover:text-vscode-accent transition-colors inline-flex items-center gap-2 text-sm"
							>
								Edit on GitHub
								<ExternalLink className="w-4 h-4" />
							</a>
						</div>
					</div>
				</article>

				{/* Sidebar Navigation (Future Enhancement) */}
				<div className="mt-8 p-6 bg-vscode-sidebar border border-vscode-border rounded-lg">
					<h3 className="text-lg font-semibold text-vscode-text-bright mb-4">Related Pages</h3>
					<p className="text-sm text-vscode-text-muted">
						Browse more documentation from the{' '}
						<button
							onClick={() => navigate('/docs')}
							className="text-vscode-accent hover:underline"
						>
							documentation home
						</button>
					</p>
				</div>
			</div>
		</div>
	)
}
